/* eslint-disable @typescript-eslint/no-unused-vars */

interface LocalStorageConfig {
  /** The location key of the data value within storage. */
  key: string;
  /** Flag indicating that this item should be skipped when programmatically clearing storage.
   * @default is `false`.
   */
  preventClear?: boolean;
}

interface LocalStorageConfigs {
  /** The areas of interest that are selectable by the user. */
  areasOfInterest: LocalStorageConfig;
  /** The type of authentication that should be used to identify the user. */
  authType: LocalStorageConfig;
  /** BusinessUnits received from the CRM upon login setup. */
  businessUnits: LocalStorageConfig;
  /** DisqualifyingReasons received from the CRM upon login setup. */
  disqualifyingReasons: LocalStorageConfig;
  /** Dynamic Content received from the CRM and refreshed upon SignalR topic. */
  dynamicContent: LocalStorageConfig;
  /** Centers received from the CRM upon login setup. */
  centers: LocalStorageConfig;
  /** The IVR state of the user in the ivr, refreshed on long poll response. */
  ivrState: LocalStorageConfig;
  /** LeadResultTypes received from the CRM upon login setup. */
  leadResultTypes: LocalStorageConfig;
  /** NotOfferedProcedures received from the CRM upon login setup. */
  notOfferedProcedures: LocalStorageConfig;
  /** Notifications prompted to the user. */
  notifications: LocalStorageConfig;
  /** A list of session tabs, including the instigating lead and operational state props. */
  sessionTabs: LocalStorageConfig;
  /** SignalRConnectionInfo queried from the server and used to establish the websocket. */
  signalRConnectionInfo: LocalStorageConfig;
  /** Token received from the CRM upon login and refreshed upon expiration. */
  token: LocalStorageConfig;
  /** User received from the CRM upon login setup. */
  user: LocalStorageConfig;
  /** The version number identified in the application configuration. */
  versionNumber: LocalStorageConfig;
}

/** Configurations for static cached objects using static keys. */
export const LocalStorageConfigs: LocalStorageConfigs = {
  areasOfInterest: { key: "areasOfInterest" },
  authType: { key: "authType" },
  businessUnits: { key: "businessUnits" },
  disqualifyingReasons: { key: "disqualifyingReasons" },
  dynamicContent: { key: "dynamicContent" },
  centers: { key: "centers" },
  ivrState: { key: "ivrState" },
  leadResultTypes: { key: "leadResultTypes" },
  notOfferedProcedures: { key: "notOfferedProcedures" },
  notifications: { key: "notifications", preventClear: true },
  sessionTabs: { key: "sessionTabs" },
  signalRConnectionInfo: { key: "signalRConnectionInfo" },
  token: { key: "token" },
  user: { key: "user" },
  versionNumber: { key: "versionNo", preventClear: true }
};

/** Save the given JSON to local storage using the given key. */
export const saveToLocal = (obj: unknown, key: string): void => localStorage.setItem(key, JSON.stringify(obj));

/** Attempt to load an object from the cache with the given key. */
export const loadFromLocal = <T>(key: string): T | undefined => {
  const value = JSON.parse(localStorage.getItem(key) as string);
  return !value ? undefined : (value as T);
};

/** Clear local storage respecting established configurations. */
export const clearLocalStorage = (): void => {
  const configs = Object.values(LocalStorageConfigs);
  Object.keys(localStorage).forEach((key: string) => {
    if (key && !configs.find(c => c.key === key)?.preventClear) localStorage.removeItem(key);
  });
  sessionStorage.clear();
};
